import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use(config => {
  const token = localStorage.getItem("fs_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem("fs_token");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export const dashboardApi = {
  getOverview: () => api.get("/dashboard/overview"),
  getAlerts: () => api.get("/dashboard/alerts"),
};

export const crewApi = {
  getAll: (params) => api.get("/crew", { params }),
  getOne: (id) => api.get(`/crew/${id}`),
  getReplacements: (id) => api.get(`/crew/${id}/replacements`),
};

export const engineeringApi = {
  getEngineers: () => api.get("/engineering/engineers"),
  getEngineer: (id) => api.get(`/engineering/engineers/${id}`),
  getTasks: () => api.get("/engineering/tasks"),
  assignTask: (taskId, engineerId) => api.put(`/engineering/tasks/${taskId}/assign`, { engineer_id: engineerId }),
};

export const alertsApi = {
  getAll: (params) => api.get("/alerts", { params }),
  update: (id, data) => api.put(`/alerts/${id}`, data),
  create: (data) => api.post("/alerts", data),
};

export const irropsApi = {
  getEvents: () => api.get("/irrops/events"),
  getRecovery: (id) => api.get(`/irrops/events/${id}/recovery`),
  createEvent: (data) => api.post("/irrops/events", data),
};

export const safetyApi = {
  getAnalytics: () => api.get("/safety/analytics"),
  getReports: () => api.get("/safety/reports"),
};

export const camoApi = {
  // Core
  getOverview:  ()             => api.get("/camo/overview"),
  getAircraft:  ()             => api.get("/camo/aircraft"),
  // Tasks
  getTasks:     (params)       => api.get("/camo/tasks", { params }),
  createTask:   (data)         => api.post("/camo/tasks", data),
  updateTask:   (id, data)     => api.put(`/camo/tasks/${id}`, data),
  deleteTask:   (id)           => api.delete(`/camo/tasks/${id}`),
  // ARC
  getArc:       ()             => api.get("/camo/arc"),
  createArc:    (data)         => api.post("/camo/arc", data),
  updateArc:    (id, data)     => api.put(`/camo/arc/${id}`, data),
  // MEL
  getMel:       (params)       => api.get("/camo/mel", { params }),
  createMel:    (data)         => api.post("/camo/mel", data),
  updateMel:    (id, data)     => api.put(`/camo/mel/${id}`, data),
  // AMP
  getAmp:       ()             => api.get("/camo/amp"),
};

export const backofficeApi = {
  getDesks: () => api.get("/backoffice/desks"),
};

export const groundopsApi = {
  getStations: () => api.get("/groundops/stations"),
};

export const adminApi = {
  getUsers: () => api.get("/admin/users"),
  getSettings: () => api.get("/admin/settings"),
  updateSettings: (data) => api.put("/admin/settings", data),
};

export const aiApi = {
  analyze: (data) => api.post("/ai/analyze", data),
};

// ─── Crew Operations (Rostering) ────────────────────────────────────
export const crewOpsApi = {
  // Crew Master
  getCrew: (params) => api.get("/crew-ops/crew", { params }),
  getCrewProfile: (id) => api.get(`/crew-ops/crew/${id}`),
  getCrewFullProfile: (id) => api.get(`/crew-ops/crew/${id}/full-profile`),
  getCrewAssignments: (id, rosterId) => api.get(`/crew-ops/crew/${id}/assignments`, { params: { roster_id: rosterId } }),
  // Pairings
  getPairings: (params) => api.get("/crew-ops/pairings", { params }),
  getPairing: (id) => api.get(`/crew-ops/pairings/${id}`),
  createPairing: (data) => api.post("/crew-ops/pairings", data),
  // Fleet Aircraft
  getFleet: () => api.get("/crew-ops/fleet"),
  // Fleet Optimisation (AI)
  optimizeFleet: (forceRefresh = false) => api.post("/crew-ops/optimize-fleet", { force_refresh: forceRefresh }),
  getOptimizationResult: () => api.get("/crew-ops/optimization-result"),
  // Pairing Fleet Settings & Utilization
  getPairingSettings: () => api.get("/crew-ops/pairing/settings"),
  updatePairingSettings: (data) => api.post("/crew-ops/pairing/settings", data),
  getUtilization: (base) => api.get("/crew-ops/pairing/utilization", { params: base ? { base } : {} }),
  // Pairing Scenarios
  savePairingScenario: (data) => api.post("/crew-ops/pairing/scenarios", data),
  listPairingScenarios: (base) => api.get("/crew-ops/pairing/scenarios", { params: base ? { base } : {} }),
  deletePairingScenario: (id) => api.delete(`/crew-ops/pairing/scenarios/${id}`),
  // Scenario Rostering Workflow
  submitScenarioToRostering: (scenarioId) => api.post(`/crew-ops/pairing/scenarios/${scenarioId}/submit`),
  listSubmissions: (params) => api.get("/crew-ops/pairing/submissions", { params }),
  getSubmission: (id) => api.get(`/crew-ops/pairing/submissions/${id}`),
  approveSubmission: (id, data) => api.post(`/crew-ops/pairing/submissions/${id}/approve`, data),
  rejectSubmission: (id, data) => api.post(`/crew-ops/pairing/submissions/${id}/reject`, data),
  deploySubmission: (id, data) => api.post(`/crew-ops/pairing/submissions/${id}/deploy`, data),
  // Monthly Scheduler
  generateMonthlySchedule: (scenarioId, data) => api.post(`/crew-ops/pairing/scenarios/${scenarioId}/generate-monthly`, data),
  listMonthlySchedules: (params) => api.get("/crew-ops/monthly-schedules", { params }),
  getMonthlySchedule: (id) => api.get(`/crew-ops/monthly-schedules/${id}`),
  allocateMonthlyCrewAll: (id) => api.post(`/crew-ops/monthly-schedules/${id}/allocate-crew`),
  approveBaseSchedule: (id, data) => api.post(`/crew-ops/monthly-schedules/${id}/approve-base`, data),
  publishFleetSchedule: (data) => api.post("/crew-ops/fleet-schedule/publish", data),
  getFleetRoster: (params) => api.get("/crew-ops/fleet-roster", { params }),
  // Rosters
  getRosters: () => api.get("/crew-ops/rosters"),
  getRosterFull: (id) => api.get(`/crew-ops/rosters/${id}/full`),
  autoAssign: (id) => api.post(`/crew-ops/rosters/${id}/auto-assign`),
  overrideAssignment: (id, data) => api.put(`/crew-ops/assignments/${id}/override`, data),
  // Compliance
  getRules: () => api.get("/crew-ops/compliance/rules"),
  getViolations: (params) => api.get("/crew-ops/compliance/violations", { params }),
  runComplianceCheck: (rosterId) => api.post(`/crew-ops/compliance/check/${rosterId}`),
  // Recovery
  getRecoveryCases: () => api.get("/crew-ops/recovery/cases"),
  getRecoveryOptions: (caseId) => api.get(`/crew-ops/recovery/cases/${caseId}/options`),
  takeRecoveryAction: (data) => api.post("/crew-ops/recovery/action", data),
  // Stats & Audit
  getStats: () => api.get("/crew-ops/stats/overview"),
  getAuditLog: (params) => api.get("/crew-ops/audit", { params }),
  // Leave
  getLeave: (params) => api.get("/crew-ops/leave", { params }),
  createLeave: (data) => api.post("/crew-ops/leave", data),
  // Scenario Simulator
  getScenarios: () => api.get("/crew-ops/scenarios"),
  getScenario: (id) => api.get(`/crew-ops/scenarios/${id}`),
  createScenario: (data) => api.post("/crew-ops/scenarios", data),
  addScenarioEvent: (id, data) => api.post(`/crew-ops/scenarios/${id}/events`, data),
  simulateScenario: (id) => api.post(`/crew-ops/scenarios/${id}/simulate`),
  deleteScenario: (id) => api.delete(`/crew-ops/scenarios/${id}`),
  // Crew Deployment
  listDeployments: (params) => api.get("/crew-ops/deployments", { params }),
  createDeployment: (data) => api.post("/crew-ops/deployments", data),
  updateDeployment: (id, data) => api.put(`/crew-ops/deployments/${id}`, data),
  deleteDeployment: (id) => api.delete(`/crew-ops/deployments/${id}`),
};

export const staffingApi = {
  getProfiles: () => api.get("/staffing/profiles"),
  getExchangeRate: () => api.get("/staffing/exchange-rate"),
  aiSuggest: (data) => api.post("/staffing/ai-suggest", data),
  calculate: (data) => api.post("/staffing/calculate", data),
  saveRun: (data) => api.post("/staffing/runs/save", data),
  getRuns: () => api.get("/staffing/runs"),
  deleteRun: (id) => api.delete(`/staffing/runs/${id}`),
};

export const disruptionApi = {
  getEvents: (params) => api.get("/disruption/events", { params }),
  getEvent: (id) => api.get(`/disruption/events/${id}`),
  createEvent: (data) => api.post("/disruption/events", data),
  analyzeEvent: (id) => api.post(`/disruption/events/${id}/analyze`),
  resolveEvent: (id, data) => api.post(`/disruption/events/${id}/resolve`, data),
  deleteEvent: (id) => api.delete(`/disruption/events/${id}`),
  getStats: () => api.get("/disruption/stats"),
  getRosterImpact: (id) => api.get(`/disruption/events/${id}/roster-impact`),
  getEligibleReserves: (id) => api.get(`/disruption/events/${id}/eligible-reserves`),
};

export const flightOpsApi = {
  getLive:               (force = false)        => api.get(`/flight-ops/live${force ? "?force_refresh=true" : ""}`),
  getStatus:             ()                     => api.get("/flight-ops/status"),
  getAirports:           ()                     => api.get("/flight-ops/airports"),
  getSimulatedAssignments: (force = false)      => api.get(`/flight-ops/simulated-assignments${force ? "?force_refresh=true" : ""}`),
  triggerDisruption:     (flightIata, data)     => api.post(`/flight-ops/flights/${flightIata}/trigger-disruption`, data),
};

export const trainingApi = {
  getOverview:         ()            => api.get("/training/overview"),
  getExpiring:         (days = 90)   => api.get(`/training/expiring?days_ahead=${days}`),
  getComplianceMatrix: ()            => api.get("/training/compliance-matrix"),
  getSchedule:         ()            => api.get("/training/schedule"),
  getInstructors:      (params)      => api.get("/training/instructors", { params }),
  // Wizard
  getWizardModules:    ()            => api.get("/training/wizard/modules"),
  suggestSlots:        (params)      => api.get("/training/wizard/suggest-slots", { params }),
  scheduleBlock:       (data)        => api.post("/training/wizard/schedule", data),
};

export const fairnessApi = {
  getScores: () => api.get("/crew-ops/fairness/scores"),
};

export const demoApi = {
  getStatus:       ()  => api.get("/demo/status"),
  getScenarios:    ()  => api.get("/demo/scenarios"),
  reset:           ()  => api.post("/demo/reset"),
  injectScenario:  (id) => api.post(`/demo/scenario/${id}`),
  reintegrate:     ()  => api.post("/demo/aog/reintegrate"),
  notifyRostering: ()  => api.post("/demo/aog/notify-rostering"),
  niaFindStandby:  ()  => api.post("/demo/nia/find-standby"),
  niaAssign:       ()  => api.post("/demo/nia/assign-standby"),
};

export const dgcaApi = {
  getCumulativeFdp:   () => api.get("/dgca/cumulative-fdp"),
  getWoclDuties:      () => api.get("/dgca/wocl-duties"),
  getSummary:         () => api.get("/dgca/summary"),
  getAugmentedCrew:   () => api.get("/dgca/augmented-crew"),
  getAcclimatisation: () => api.get("/dgca/acclimatisation"),
};

export const opsConsoleApi = {
  getStatus:           (base) => api.get("/ops-console/status", { params: base ? { base } : {} }),
  getCrewAvailability: ()     => api.get("/ops-console/crew-availability"),
  swapCrew:            (data) => api.post("/ops-console/swap", data),
  getSwapHistory:      ()     => api.get("/ops-console/swap-history"),
};

export const swapRequestsApi = {
  list:          (params)   => api.get("/swaps", { params }),
  pendingCount:  ()         => api.get("/swaps/pending-count"),
  submit:        (data)     => api.post("/swaps", data),
  respond:       (id, data) => api.post(`/swaps/${id}/respond`, data),
  review:        (id, data) => api.post(`/swaps/${id}/review`, data),
  cancel:        (id)       => api.delete(`/swaps/${id}`),
};

export const rosterExportApi = {
  getRosterFull: (rosterId) => api.get(`/crew-ops/rosters/${rosterId}/full`),
  getAssignments:(params)   => api.get("/crew-ops/assignments", { params }),
};

export const integrationsApi = {
  getStatus:  ()   => api.get("/integrations/status"),
  getData:    (id) => api.get(`/integrations/${id}/data`),
  triggerSync:(id) => api.post(`/integrations/${id}/sync`),
};

export const frmsApi = {
  getOverview:      ()     => api.get("/frms/overview"),
  getPolicyDocs:    ()     => api.get("/frms/policy-docs"),
  getHazardRegister:()     => api.get("/frms/hazard-register"),
  getOccurrences:   ()     => api.get("/frms/occurrences"),
  submitOccurrence: (data) => api.post("/frms/occurrences", data),
  getAudits:        ()     => api.get("/frms/audits"),
  getScientificEvidence: () => api.get("/frms/scientific-evidence"),
  getMarketingData: ()     => api.get("/frms/marketing-data"),
};

export const pricingApi = {
  listScenarios:   ()     => api.get("/pricing/scenarios"),
  createScenario:  (data) => api.post("/pricing/scenarios", data),
  updateScenario:  (id, data) => api.put(`/pricing/scenarios/${id}`, data),
  deleteScenario:  (id)   => api.delete(`/pricing/scenarios/${id}`),
};

export const groundOpsApi = {
  seed:              ()                => api.post("/ground-ops/seed").then(r => r.data),
  listFlights:       (params)          => api.get("/ground-ops/flights", { params }).then(r => r.data),
  getFlight:         (id)              => api.get(`/ground-ops/flights/${id}`).then(r => r.data),
  getFlightTasks:    (flightId)        => api.get(`/ground-ops/flights/${flightId}/tasks`).then(r => r.data),
  updateTask:        (taskId, data)    => api.patch(`/ground-ops/tasks/${taskId}`, data).then(r => r.data),
  assignTask:        (taskId, name)    => api.patch(`/ground-ops/tasks/${taskId}/assign`, { assigned_to: name }).then(r => r.data),
  getStaff:          (station)         => api.get("/ground-ops/staff", { params: station ? { station } : {} }).then(r => r.data),
  getAvailableTasks:   (station, dept)   => api.get("/ground-ops/tasks/available", { params: { station, department: dept } }).then(r => r.data),
  getShiftOverview:  (station)         => api.get("/ground-ops/shift/overview", { params: station ? { station } : {} }).then(r => r.data),
  allocateStaff:     (staffName, data) => api.post(`/ground-ops/staff/${encodeURIComponent(staffName)}/allocate-flight`, data).then(r => r.data),
  getStaffPlanning:  (station)         => api.get("/ground-ops/staff/planning", { params: station ? { station } : {} }).then(r => r.data),
  getFatigue:        (station)         => api.get("/ground-ops/fatigue", { params: station ? { station } : {} }).then(r => r.data),
  getExceptions:     ()                => api.get("/ground-ops/exceptions").then(r => r.data),
  getAlerts:         ()                => api.get("/ground-ops/alerts").then(r => r.data),
  getPerformance:    ()                => api.get("/ground-ops/performance").then(r => r.data),
  listIncidents:     (station)         => api.get("/ground-ops/incidents", { params: station ? { station } : {} }).then(r => r.data),
  createIncident:    (data)            => api.post("/ground-ops/incidents", data).then(r => r.data),
  getMyTasks:        (role, station)   => api.get("/ground-ops/my-tasks", { params: { role, station } }).then(r => r.data),
  // Phase 2
  predictDeparture:  (flightId)        => api.post(`/ground-ops/flights/${flightId}/predict`).then(r => r.data),
  getLatestPrediction: (flightId)      => api.get(`/ground-ops/flights/${flightId}/prediction`).then(r => r.data),
  getVendorSla:      ()                => api.get("/ground-ops/vendor-sla").then(r => r.data),
  getVendorDetail:   (name)            => api.get(`/ground-ops/vendor-sla/${encodeURIComponent(name)}`).then(r => r.data),
};

// ─── Demo Requests API ─────────────────────────────────────────────────────
export const demoRequestsApi = {
  getAll:       ()          => api.get("/demo-requests"),
  getUnreadCount: ()        => api.get("/demo-requests/unread-count"),
  markRead:     (id)        => api.patch(`/demo-requests/${id}/read`),
  updateStatus: (id, status) => api.patch(`/demo-requests/${id}/status`, { status }),
};

export default api;
